import React from 'react';
import { Proposal } from '../models/Proposal';
import { CustomMeal } from '../models/CustomMeal';
import { debug } from '../utils/debug';

const ProposalList = ({ 
  proposals = [], 
  selectedProposal, 
  onProposalSelect,
  variant = 'compact', // 'compact' or 'card'
  currentUser
}) => {
  if (!proposals || proposals.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No proposals found. Create a new proposal to get started!
      </div>
    );
  }

  const renderCompactItem = (proposal) => {
    if (!proposal || !(proposal instanceof Proposal)) {
      debug.error('Invalid proposal object:', proposal);
      return null;
    }

    try {
      const isSelected = selectedProposal?.id === proposal.id;
      const mealIcon = proposal.meal ? new CustomMeal(proposal.meal).getIconName() : 'table_restaurant';
      const mealName = proposal.meal ? new CustomMeal(proposal.meal).getDescription() : '';
      const costIndicator = proposal.getCostIndicator();

      const getStateIcon = () => {
        switch (proposal.getStateEnum()) {
          case Proposal.ProposalState.DECIDED:
            return 'check_circle';
          case Proposal.ProposalState.CANCELED:
            return 'cancel';
          case Proposal.ProposalState.SORTING:
            return 'sort';
          default:
            return 'schedule';
        }
      };

      return (
        <div
          key={proposal.id}
          onClick={() => onProposalSelect?.(proposal)}
          className={`p-2 cursor-pointer transition-colors duration-200 ${
            isSelected ? 'bg-accent text-white' : 'hover:bg-beige'
          }`}
        >
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center border-2 rounded ${
              isSelected ? 'border-white' : 'border-accent'
            }`}>
              <span className={`material-symbols-outlined !text-4xl ${
                isSelected ? 'text-white' : 'text-accent'
              }`}>
                {mealIcon}
              </span>
            </div>
            <div className="flex-grow min-w-0 ml-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-base">{proposal.generateDescription()}</span>
                <span className={`text-base ${isSelected ? 'text-white' : 'text-accent'}`}>
                  {costIndicator}
                </span>
              </div>
              <div className="text-sm mt-1">
                <span className={`${isSelected ? 'text-white' : 'text-gray-600'}`}>
                  {new Date(proposal.eventtime).toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center ml-2">
              <span className={`material-symbols-outlined !text-2xl ${
                isSelected ? 'text-white' : 'text-accent'
              }`}>
                {getStateIcon()}
              </span>
            </div>
            {proposal.owner === currentUser?.uid && (
              <div className="absolute top-0 right-0 -mt-1 -mr-1">
                <span className="material-icons !text-lg text-[#B81F1F]">
                  star
                </span>
              </div>
            )}
          </div>
        </div>
      );
    } catch (error) {
      debug.error('Error rendering proposal item:', error);
      return null;
    }
  };

  const renderCardItem = (proposal) => {
    if (!proposal || !(proposal instanceof Proposal)) {
      return null;
    }

    const mealIcon = proposal.meal ? new CustomMeal(proposal.meal).getIconName() : 'table_restaurant';
    const isSelected = selectedProposal?.id === proposal.id;

    return (
      <div 
        key={proposal.id} 
        onClick={() => onProposalSelect?.(proposal)}
        className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${
          isSelected ? 'ring-2 ring-accent' : ''
        }`}
      >
        <div className="flex items-start relative">
          {/* Left icon (meal type) */}
          <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center ml-2 mt-2 mb-2 border-2 rounded ${
            isSelected ? 'border-white' : 'border-accent'
          }`}>
            <span className={`material-symbols-outlined !text-3xl ${
              isSelected ? 'text-white' : 'text-accent'
            }`}>
              {mealIcon}
            </span>
          </div>

          {/* Main content area */}
          <div className="flex-grow min-w-0 ml-2">
            <h3 className="font-medium text-base">{proposal.generateDescription()}</h3>
            <p className="text-sm text-gray-600">{new Date(proposal.eventtime).toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })}</p>
            <p className="text-sm text-gray-600">Cost: {proposal.getCostIndicator()}</p>
          </div>

          {/* Owner star icon */}
          {proposal.owner === currentUser?.uid && (
            <div className="absolute top-0 right-0 -mt-1 -mr-1">
              <span className="material-icons !text-lg text-[#B81F1F]">
                star
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderItem = variant === 'compact' ? renderCompactItem : renderCardItem;

  return (
    <div className={variant === 'compact' ? 'space-y-1' : 'grid gap-4 grid-cols-1 sm:grid-cols-2'}>
      {proposals.map(renderItem)}
    </div>
  );
};

export default ProposalList; 